import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  Progress,
  Switch,
  message
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  listFileCategories,
  createFileCategory,
  updateFileCategory,
  deleteFileCategory,
  listFileGroups,
  createFileGroup,
  updateFileGroup,
  deleteFileGroup,
  listManagedFiles,
  uploadManagedFile,
  deleteManagedFile,
  subscribeUploadProgress
} from '../api/fileManagementApi';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

const formatBytes = (value) => {
  if (!value || Number.isNaN(Number(value))) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = Number(value);
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size % 1 === 0 ? size : size.toFixed(1)} ${units[unitIndex]}`;
};

const normalizeExtensions = (extensions) => {
  if (!extensions) return [];
  if (Array.isArray(extensions)) {
    return extensions.map((ext) => ext?.trim()).filter(Boolean);
  }
  return String(extensions)
    .split(',')
    .map((ext) => ext.trim())
    .filter(Boolean);
};

const displayExtensions = (extensions) => {
  const list = normalizeExtensions(extensions);
  if (!list.length) {
    return <Text type="secondary">제한 없음</Text>;
  }
  return (
    <Space size={[4, 4]} wrap>
      {list.map((ext) => (
        <Tag key={ext}>{ext.startsWith('.') ? ext : `.${ext}`}</Tag>
      ))}
    </Space>
  );
};

const mapFormExtensions = (extensions) => normalizeExtensions(extensions).join(', ');

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = dayjs(value);
  if (!d.isValid()) {
    return value;
  }
  return d.format('YYYY-MM-DD HH:mm');
};

const createUploadId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `upload_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const initialUploadProgressState = {
  enabled: false,
  status: 'IDLE',
  totalBytes: 0,
  uploadedBytes: 0,
  currentFile: null,
  message: null,
  uploadId: null,
};

const FileManagement = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [files, setFiles] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [categoryModal, setCategoryModal] = useState({ open: false, mode: 'create', target: null });
  const [groupModal, setGroupModal] = useState({ open: false, mode: 'create', target: null });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);

  const [uploadProgress, setUploadProgress] = useState(initialUploadProgressState);
  const uploadEventSourceRef = useRef(null);

  const [categoryForm] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [uploadForm] = Form.useForm();

  const closeUploadProgress = useCallback(() => {
    if (uploadEventSourceRef.current) {
      try {
        uploadEventSourceRef.current.close();
      } catch (_) {
        // ignore close errors
      }
      uploadEventSourceRef.current = null;
    }
    setUploadProgress(initialUploadProgressState);
  }, []);

  const resetUploadState = useCallback(() => {
    setUploadFileList([]);
    uploadForm.resetFields();
    closeUploadProgress();
  }, [uploadForm, closeUploadProgress]);

  const startUploadProgress = useCallback(async (uploadId) => {
    try {
      const source = await subscribeUploadProgress(uploadId, {
        onEvent: (type, payload) => {
          setUploadProgress((prev) => {
            const totalBytes = payload?.aggregatedTotalBytes ?? prev.totalBytes;
            const uploadedBytes = payload?.aggregatedUploadedBytes ?? prev.uploadedBytes;
            const nextState = {
              enabled: true,
              status: type,
              totalBytes,
              uploadedBytes,
              currentFile: payload?.fileName ?? prev.currentFile,
              message: payload?.message ?? (type === 'FAILED' ? '업로드에 실패했습니다.' : prev.message),
              uploadId,
            };
            if (type === 'COMPLETED') {
              nextState.uploadedBytes = totalBytes || uploadedBytes;
            }
            return nextState;
          });
          if (type === 'COMPLETED') {
            setTimeout(() => {
              closeUploadProgress();
            }, 1500);
          }
        },
        onError: () => {
          setUploadProgress((prev) => ({
            ...prev,
            enabled: true,
            status: 'DISCONNECTED',
            message: '진행률 연결이 종료되었습니다.',
          }));
        },
      });
      uploadEventSourceRef.current = source;
      setUploadProgress({
        enabled: true,
        status: 'WAITING',
        totalBytes: 0,
        uploadedBytes: 0,
        currentFile: null,
        message: null,
        uploadId,
      });
      return true;
    } catch (error) {
      console.warn('업로드 진행률 구독 실패:', error);
      setUploadProgress(initialUploadProgressState);
      return false;
    }
  }, [closeUploadProgress]);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const list = await listFileCategories({ size: 200 });
      const items = Array.isArray(list) ? list : [];
      setCategories(items);
      if (!items.length) {
        setSelectedCategoryId(null);
        setGroups([]);
        setSelectedGroupId(null);
        setFiles([]);
        return;
      }
      const exists = items.some((item) => item.fileCategoryId === selectedCategoryId);
      if (!exists) {
        setSelectedCategoryId(items[0].fileCategoryId);
      }
    } catch (error) {
      console.warn('파일 카테고리 조회 실패:', error);
      message.error('파일 카테고리를 불러오지 못했습니다. 권한 또는 네트워크 상태를 확인하세요.');
      setCategories([]);
      setSelectedCategoryId(null);
      setGroups([]);
      setSelectedGroupId(null);
      setFiles([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [selectedCategoryId]);

  const loadGroups = useCallback(async (categoryId) => {
    if (!categoryId) {
      setGroups([]);
      setSelectedGroupId(null);
      return;
    }
    setLoadingGroups(true);
    try {
      const list = await listFileGroups(categoryId);
      const items = Array.isArray(list) ? list : [];
      setGroups(items);
      if (items.length === 0) {
        setSelectedGroupId(null);
        return;
      }
      const exists = items.some((item) => item.fileGroupId === selectedGroupId);
      if (!exists) {
        setSelectedGroupId(items[0].fileGroupId);
      }
    } catch (error) {
      console.warn('파일 그룹 조회 실패:', error);
      message.error('파일 그룹을 불러오지 못했습니다.');
      setGroups([]);
      setSelectedGroupId(null);
    } finally {
      setLoadingGroups(false);
    }
  }, [selectedGroupId]);

  const loadFiles = useCallback(async (groupId) => {
    if (!groupId) {
      setFiles([]);
      return;
    }
    setLoadingFiles(true);
    try {
      const list = await listManagedFiles(groupId);
      setFiles(Array.isArray(list) ? list : []);
    } catch (error) {
      console.warn('파일 목록 조회 실패:', error);
      message.error('파일 목록을 불러오지 못했습니다.');
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (selectedCategoryId) {
      loadGroups(selectedCategoryId);
    } else {
      setGroups([]);
      setSelectedGroupId(null);
      setFiles([]);
    }
  }, [selectedCategoryId, loadGroups]);

  useEffect(() => {
    if (selectedGroupId) {
      loadFiles(selectedGroupId);
    } else {
      setFiles([]);
    }
  }, [selectedGroupId, loadFiles]);

  useEffect(() => {
    if (categoryModal.open) {
      const target = categoryModal.target;
      categoryForm.setFieldsValue({
        categoryCode: target?.categoryCode ?? '',
        categoryName: target?.categoryName ?? '',
        description: target?.description ?? '',
        storageBasePath: target?.storageBasePath ?? '',
        allowedExtensions: mapFormExtensions(target?.allowedExtensions),
        maxFileSizeMb: target?.maxFileSizeBytes ? Number((target.maxFileSizeBytes / (1024 * 1024)).toFixed(2)) : null,
        maxGroupSizeMb: target?.maxGroupSizeBytes ? Number((target.maxGroupSizeBytes / (1024 * 1024)).toFixed(2)) : null,
        generateThumbnail: target?.generateThumbnail ?? false
      });
    } else {
      categoryForm.resetFields();
    }
  }, [categoryModal, categoryForm]);

  useEffect(() => {
    if (groupModal.open) {
      const target = groupModal.target;
      groupForm.setFieldsValue({
        groupName: target?.groupName ?? '',
        description: target?.description ?? '',
        storagePath: target?.storagePath ?? '',
        sortOrder: target?.sortOrder ?? null
      });
    } else {
      groupForm.resetFields();
    }
  }, [groupModal, groupForm]);

  useEffect(() => {
    if (!uploadModalOpen) {
      resetUploadState();
    }
  }, [uploadModalOpen, resetUploadState]);

  useEffect(() => () => {
    closeUploadProgress();
  }, [closeUploadProgress]);

  const selectedCategory = useMemo(
    () => categories.find((item) => item.fileCategoryId === selectedCategoryId) || null,
    [categories, selectedCategoryId]
  );

  const selectedGroup = useMemo(
    () => groups.find((item) => item.fileGroupId === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  const uploadProgressPercent = useMemo(() => {
    if (!uploadProgress.enabled) return 0;
    if (!uploadProgress.totalBytes) {
      return uploadProgress.status === 'COMPLETED' ? 100 : 0;
    }
    return Math.min(100, Math.round((uploadProgress.uploadedBytes / uploadProgress.totalBytes) * 100));
  }, [uploadProgress]);

  const openCategoryModal = (mode, target = null) => {
    setCategoryModal({ open: true, mode, target });
  };

  const openGroupModal = (mode, target = null) => {
    setGroupModal({ open: true, mode, target });
  };

  const closeCategoryModal = () => setCategoryModal({ open: false, mode: 'create', target: null });
  const closeGroupModal = () => setGroupModal({ open: false, mode: 'create', target: null });

  const handleSubmitCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      const allowedExtensions = normalizeExtensions(values.allowedExtensions).map((ext) => {
        if (!ext) return null;
        const trimmed = ext.startsWith('.') ? ext.slice(1) : ext;
        return trimmed.toLowerCase();
      }).filter(Boolean);
      const payload = {
        categoryCode: values.categoryCode.trim(),
        categoryName: values.categoryName.trim(),
        description: values.description?.trim() || null,
        storageBasePath: values.storageBasePath.trim(),
        allowedExtensions,
        maxFileSizeBytes: values.maxFileSizeMb ? Math.round(Number(values.maxFileSizeMb) * 1024 * 1024) : null,
        maxGroupSizeBytes: values.maxGroupSizeMb ? Math.round(Number(values.maxGroupSizeMb) * 1024 * 1024) : null,
        generateThumbnail: values.generateThumbnail === true
      };

      setSavingCategory(true);
      if (categoryModal.mode === 'edit' && categoryModal.target?.fileCategoryId) {
        await updateFileCategory(categoryModal.target.fileCategoryId, payload);
        message.success('카테고리를 수정했습니다.');
      } else {
        await createFileCategory(payload);
        message.success('카테고리를 등록했습니다.');
      }
      closeCategoryModal();
      await loadCategories();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.warn('카테고리 저장 실패:', error);
    } finally {
      setSavingCategory(false);
    }
  };

  const handleSubmitGroup = async () => {
    if (!selectedCategoryId && groupModal.mode !== 'edit') {
      message.warning('먼저 카테고리를 선택하세요.');
      return;
    }
    try {
      const values = await groupForm.validateFields();
      const payload = {
        groupName: values.groupName.trim(),
        description: values.description?.trim() || null,
        storagePath: values.storagePath?.trim() || null,
        sortOrder: values.sortOrder != null ? Number(values.sortOrder) : null
      };
      setSavingGroup(true);
      if (groupModal.mode === 'edit' && groupModal.target?.fileGroupId) {
        await updateFileGroup(groupModal.target.fileGroupId, payload);
        message.success('파일 그룹을 수정했습니다.');
      } else {
        await createFileGroup(selectedCategoryId, payload);
        message.success('파일 그룹을 생성했습니다.');
      }
      closeGroupModal();
      if (groupModal.mode === 'edit' && groupModal.target?.fileGroupId) {
        await loadGroups(selectedCategoryId);
      } else {
        await loadGroups(selectedCategoryId);
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.warn('파일 그룹 저장 실패:', error);
    } finally {
      setSavingGroup(false);
    }
  };

  const confirmDeleteCategory = (category) => {
    Modal.confirm({
      title: '카테고리를 삭제하시겠습니까?',
      content: (
        <span>
          <Text strong>{category.categoryName}</Text> 카테고리를 삭제하면 하위 그룹과 파일도 함께 삭제됩니다.
        </span>
      ),
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      async onOk() {
        try {
          await deleteFileCategory(category.fileCategoryId);
          message.success('카테고리를 삭제했습니다.');
          await loadCategories();
        } catch (error) {
          console.warn('카테고리 삭제 실패:', error);
        }
      }
    });
  };

  const confirmDeleteGroup = (group) => {
    Modal.confirm({
      title: '파일 그룹을 삭제하시겠습니까?',
      content: (
        <span>
          <Text strong>{group.groupName}</Text> 그룹과 포함된 파일이 모두 삭제됩니다.
        </span>
      ),
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      async onOk() {
        try {
          await deleteFileGroup(group.fileGroupId);
          message.success('파일 그룹을 삭제했습니다.');
          await loadGroups(selectedCategoryId);
        } catch (error) {
          console.warn('파일 그룹 삭제 실패:', error);
        }
      }
    });
  };

  const confirmDeleteFile = (file) => {
    Modal.confirm({
      title: '파일을 삭제하시겠습니까?',
      content: (
        <span>
          <Text strong>{file.originalFileName}</Text> 파일이 영구적으로 삭제됩니다.
        </span>
      ),
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      async onOk() {
        try {
          await deleteManagedFile(file.fileId);
          message.success('파일을 삭제했습니다.');
          await loadFiles(selectedGroupId);
        } catch (error) {
          console.warn('파일 삭제 실패:', error);
        }
      }
    });
  };

  const handleUploadSubmit = async () => {
    if (!selectedGroupId) {
      message.warning('먼저 파일을 추가할 그룹을 선택하세요.');
      return;
    }
    if (!uploadFileList.length) {
      message.warning('업로드할 파일을 선택하세요.');
      return;
    }
    try {
      const values = uploadForm.getFieldsValue();
      const formData = new FormData();
      uploadFileList.forEach((file) => {
        const rawFile = file.originFileObj || file;
        formData.append('files', rawFile);
      });
      const description = values.description?.trim();
      if (description) {
        uploadFileList.forEach(() => {
          formData.append('descriptions', description);
        });
      }
      const uploadId = createUploadId();
      formData.append('uploadId', uploadId);
      await startUploadProgress(uploadId);
      setUploading(true);
      await uploadManagedFile(selectedGroupId, formData);
      message.success('파일을 업로드했습니다.');
      setUploadModalOpen(false);
      await loadFiles(selectedGroupId);
      await loadGroups(selectedCategoryId);
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.warn('파일 업로드 실패:', error);
      message.error('파일 업로드에 실패했습니다.');
      closeUploadProgress();
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      setUploadFileList((prev) => {
        if (prev.some((item) => item.uid === file.uid)) {
          return prev;
        }
        return [...prev, file];
      });
      return false;
    },
    onRemove: (file) => {
      setUploadFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    fileList: uploadFileList,
    maxCount: 10,
    multiple: true,
    accept: '*',
    disabled: uploading,
  };

  const fileColumns = [
    {
      title: '파일명',
      dataIndex: 'originalFileName',
      key: 'originalFileName',
      sorter: (a, b) => (a.originalFileName || '').localeCompare(b.originalFileName || ''),
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <a href={record.downloadUrl} target="_blank" rel="noopener noreferrer">{record.originalFileName}</a>
          {record.description && <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>}
        </Space>
      )
    },
    {
      title: '크기',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 120,
      sorter: (a, b) => (a.fileSize || 0) - (b.fileSize || 0),
      render: (value) => formatBytes(value)
    },
    {
      title: '확장자',
      dataIndex: 'fileExtension',
      key: 'fileExtension',
      width: 100,
      sorter: (a, b) => (a.fileExtension || '').localeCompare(b.fileExtension || ''),
      render: (value) => value ? (value.startsWith('.') ? value : `.${value}`) : '-'
    },
    {
      title: '경로',
      dataIndex: 'storagePath',
      key: 'storagePath',
      sorter: (a, b) => (a.storagePath || '').localeCompare(b.storagePath || ''),
      render: (value) => value || '-'
    },
    {
      title: '콘텐츠 유형',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 160,
      sorter: (a, b) => (a.contentType || '').localeCompare(b.contentType || ''),
      render: (value) => value || '-'
    },
    {
      title: '썸네일',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      width: 120,
      sorter: (a, b) => (a.thumbnailUrl || '').localeCompare(b.thumbnailUrl || ''),
      render: (value) => (value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">보기</a>
      ) : '-')
    },
    {
      title: '업로드일',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 160,
      sorter: (a, b) => (a.uploadedAt || '').localeCompare(b.uploadedAt || ''),
      render: (value) => formatDateTime(value)
    },
    {
      title: '관리',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => confirmDeleteFile(record)}
          >
            삭제
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title={
          <Space size={8} align="center">
            <FolderOpenOutlined />
            <span>파일 관리</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => loadCategories()} loading={loadingCategories}>
              새로고침
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCategoryModal('create')}>
              카테고리 추가
            </Button>
          </Space>
        }
        styles={{ body: { padding: 0 } }}
      >
        <Row gutter={[16, 16]} style={{ padding: 16 }}>
          <Col xs={24} lg={8}>
            <Card
              size="small"
              title="카테고리"
              extra={
                <Space>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => loadCategories()}
                    loading={loadingCategories}
                  />
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openCategoryModal('create')}
                  >
                    추가
                  </Button>
                </Space>
              }
            >
              <Spin spinning={loadingCategories}>
                {categories.length === 0 ? (
                  <Empty description="등록된 카테고리가 없습니다." />
                ) : (
                  <List
                    dataSource={categories}
                    renderItem={(item) => {
                      const selected = item.fileCategoryId === selectedCategoryId;
                      return (
                        <List.Item
                          key={item.fileCategoryId}
                          onClick={() => setSelectedCategoryId(item.fileCategoryId)}
                          style={{
                            cursor: 'pointer',
                            borderRadius: 8,
                            border: selected ? '1px solid var(--ant-primary-color)' : '1px solid #f0f0f0',
                            backgroundColor: selected ? 'rgba(24,144,255,0.08)' : '#fff',
                            marginBottom: 12,
                            padding: 12
                          }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }} size={8}>
                            <Space align="center" justify="space-between">
                              <div>
                                <Text strong>{item.categoryName}</Text>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{item.categoryCode}</div>
                              </div>
                              <Space>
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<EditOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCategoryModal('edit', item);
                                  }}
                                />
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmDeleteCategory(item);
                                  }}
                                />
                              </Space>
                            </Space>
                            <div style={{ fontSize: 12, color: '#595959' }}>{item.description || '설명 없음'}</div>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{item.storageBasePath}</div>
                            <div>{displayExtensions(item.allowedExtensions)}</div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                              최대 용량: {item.maxFileSizeBytes ? formatBytes(item.maxFileSizeBytes) : '제한 없음'}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                              그룹 용량: {item.maxGroupSizeBytes ? formatBytes(item.maxGroupSizeBytes) : '제한 없음'}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                              썸네일: {item.generateThumbnail ? '생성' : '생성 안 함'}
                            </div>
                          </Space>
                        </List.Item>
                      );
                    }}
                  />
                )}
              </Spin>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card size="small" title="선택된 카테고리 상세">
              {selectedCategory ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Paragraph style={{ marginBottom: 0 }}>{selectedCategory.description || '설명 없음'}</Paragraph>
                  <Space size={[8, 4]} wrap>
                    <Tag color="blue">코드: {selectedCategory.categoryCode}</Tag>
                    <Tag color="geekblue">기본 경로: {selectedCategory.storageBasePath}</Tag>
                    <Tag color="purple">
                      최대 용량: {selectedCategory.maxFileSizeBytes ? formatBytes(selectedCategory.maxFileSizeBytes) : '제한 없음'}
                    </Tag>
                    <Tag color="magenta">
                      그룹 용량: {selectedCategory.maxGroupSizeBytes ? formatBytes(selectedCategory.maxGroupSizeBytes) : '제한 없음'}
                    </Tag>
                    <Tag color={selectedCategory.generateThumbnail ? 'success' : 'default'}>
                      썸네일: {selectedCategory.generateThumbnail ? '생성' : '생성 안 함'}
                    </Tag>
                  </Space>
                  <div>
                    <Text strong>허용 확장자</Text>
                    <div style={{ marginTop: 4 }}>{displayExtensions(selectedCategory.allowedExtensions)}</div>
                  </div>
                </Space>
              ) : (
                <Empty description="카테고리를 선택하세요." />
              )}
            </Card>

            <Divider style={{ margin: '16px 0' }} />

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card
                  size="small"
                  title="파일 그룹"
                  extra={
                    <Space>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => selectedCategoryId && loadGroups(selectedCategoryId)}
                        disabled={!selectedCategoryId}
                        loading={loadingGroups}
                      />
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openGroupModal('create')}
                        disabled={!selectedCategoryId}
                      >
                        그룹 추가
                      </Button>
                    </Space>
                  }
                >
                  {selectedCategoryId ? (
                    <Spin spinning={loadingGroups}>
                      {groups.length === 0 ? (
                        <Empty description="등록된 파일 그룹이 없습니다." />
                      ) : (
                        <List
                          dataSource={groups}
                          renderItem={(group) => {
                            const selected = group.fileGroupId === selectedGroupId;
                            return (
                              <List.Item
                                key={group.fileGroupId}
                                onClick={() => setSelectedGroupId(group.fileGroupId)}
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: 8,
                                  border: selected ? '1px solid var(--ant-primary-color)' : '1px solid #f0f0f0',
                                  backgroundColor: selected ? 'rgba(24,144,255,0.06)' : '#fff',
                                  marginBottom: 12,
                                  padding: 12
                                }}
                              >
                                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                  <Space align="center" justify="space-between">
                                    <div>
                                      <Text strong>{group.groupName}</Text>
                                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>{group.storagePath || '-'}</div>
                                    </div>
                                    <Space>
                                      <Button
                                        size="small"
                                        type="text"
                                        icon={<EditOutlined />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openGroupModal('edit', group);
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          confirmDeleteGroup(group);
                                        }}
                                      />
                                    </Space>
                                  </Space>
                                  <div style={{ fontSize: 12, color: '#595959' }}>{group.description || '설명 없음'}</div>
                                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>정렬 순서: {group.sortOrder ?? '-'}</div>
                                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                    파일 수: {group.fileCount ?? 0}개 · 총 용량: {group.totalFileSize ? formatBytes(group.totalFileSize) : '0 B'}
                                  </div>
                                </Space>
                              </List.Item>
                            );
                          }}
                        />
                      )}
                    </Spin>
                  ) : (
                    <Empty description="카테고리를 먼저 선택하세요." />
                  )}
                </Card>
              </Col>

              <Col span={24}>
                <Card
                  size="small"
                  title={
                    <Space align="center">
                      <span>파일 목록</span>
                      {selectedGroup && <Tag color="blue">{selectedGroup.groupName}</Tag>}
                    </Space>
                  }
                  extra={
                    <Space>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => selectedGroupId && loadFiles(selectedGroupId)}
                        disabled={!selectedGroupId}
                        loading={loadingFiles}
                      />
                      <Button
                        size="small"
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setUploadModalOpen(true)}
                        disabled={!selectedGroupId}
                      >
                        파일 업로드
                      </Button>
                    </Space>
                  }
                >
                  {selectedGroupId ? (
                    <Spin spinning={loadingFiles}>
                      <Table
                        dataSource={files}
                        columns={fileColumns}
                        rowKey={(record) => record.fileId}
                        locale={{ emptyText: '업로드된 파일이 없습니다.' }}
                        pagination={false}
                        size="small"
                      />
                    </Spin>
                  ) : (
                    <Empty description="파일 그룹을 선택하세요." />
                  )}
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Modal
        title={categoryModal.mode === 'edit' ? '파일 카테고리 수정' : '파일 카테고리 추가'}
        open={categoryModal.open}
        onCancel={closeCategoryModal}
        onOk={handleSubmitCategory}
        confirmLoading={savingCategory}
        okText={categoryModal.mode === 'edit' ? '수정' : '등록'}
        cancelText="취소"
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item
            label="카테고리 코드"
            name="categoryCode"
            rules={[{ required: true, message: '카테고리 코드를 입력하세요.' }]}
          >
            <Input placeholder="예: COMMON_DOC" maxLength={100} />
          </Form.Item>
          <Form.Item
            label="카테고리명"
            name="categoryName"
            rules={[{ required: true, message: '카테고리명을 입력하세요.' }]}
          >
            <Input placeholder="예: 공통 문서" maxLength={200} />
          </Form.Item>
          <Form.Item label="설명" name="description">
            <Input.TextArea rows={3} maxLength={500} placeholder="카테고리 설명을 입력하세요." />
          </Form.Item>
          <Form.Item
            label="저장 기본 경로"
            name="storageBasePath"
            rules={[{ required: true, message: '저장 경로를 입력하세요.' }]}
          >
            <Input placeholder="예: docs/common" maxLength={500} />
          </Form.Item>
          <Form.Item
            label="허용 확장자"
            name="allowedExtensions"
            extra="콤마로 구분하여 입력하세요. 예: pdf, xlsx, docx"
          >
            <Input placeholder="예: pdf, xlsx" maxLength={500} />
          </Form.Item>
          <Form.Item label="최대 파일 크기 (MB)" name="maxFileSizeMb">
            <InputNumber style={{ width: '100%' }} min={1} step={1} placeholder="예: 100" />
          </Form.Item>
          <Form.Item label="그룹 최대 용량 (MB)" name="maxGroupSizeMb">
            <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder="예: 500 (0은 제한 없음)" />
          </Form.Item>
          <Form.Item label="이미지 썸네일 생성" name="generateThumbnail" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={groupModal.mode === 'edit' ? '파일 그룹 수정' : '파일 그룹 추가'}
        open={groupModal.open}
        onCancel={closeGroupModal}
        onOk={handleSubmitGroup}
        confirmLoading={savingGroup}
        okText={groupModal.mode === 'edit' ? '수정' : '등록'}
        cancelText="취소"
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            label="그룹명"
            name="groupName"
            rules={[{ required: true, message: '그룹명을 입력하세요.' }]}
          >
            <Input placeholder="예: 정책 문서" maxLength={200} />
          </Form.Item>
          <Form.Item label="설명" name="description">
            <Input.TextArea rows={3} maxLength={500} placeholder="그룹 설명을 입력하세요." />
          </Form.Item>
          <Form.Item label="저장 경로" name="storagePath">
            <Input placeholder="카테고리 기본 경로 아래 상대 경로를 입력하세요." maxLength={500} />
          </Form.Item>
          <Form.Item label="정렬 순서" name="sortOrder">
            <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder="예: 0" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="파일 업로드"
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onOk={handleUploadSubmit}
        confirmLoading={uploading}
        okText="업로드"
        cancelText="취소"
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item label="파일" name="file">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">파일을 클릭하거나 드래그하여 업로드하세요.</p>
              <p className="ant-upload-hint">한 번에 여러 파일을 선택할 수 있습니다.</p>
            </Dragger>
          </Form.Item>
          <Form.Item label="설명" name="description">
            <Input.TextArea rows={3} maxLength={500} placeholder="파일 설명을 입력하세요 (선택)" />
          </Form.Item>
        </Form>
        {uploadProgress.enabled && (
          <div style={{ marginTop: 16 }}>
            <Progress
              percent={uploadProgressPercent}
              status={(() => {
                if (uploadProgress.status === 'FAILED' || uploadProgress.status === 'DISCONNECTED') return 'exception';
                if (uploadProgress.status === 'COMPLETED') return 'success';
                return 'active';
              })()}
              size="small"
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#595959' }}>
              {uploadProgress.currentFile && (
                <div>현재 파일: {uploadProgress.currentFile}</div>
              )}
              <div>
                {formatBytes(uploadProgress.uploadedBytes)} / {uploadProgress.totalBytes ? formatBytes(uploadProgress.totalBytes) : '계산 중'}
              </div>
              {uploadProgress.message && (
                <div style={{ color: '#d32029' }}>{uploadProgress.message}</div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Space>
  );
};

export default FileManagement;
